import { useEffect, useMemo, useState } from "react";
import GxRedRejectButton from "@/app/components/common/actions/buttons/GxRedRejectButton";
import GxPrimaryButton from "@/app/components/common/actions/buttons/GxPrimaryButton";
import GxHorizontalLine from "@/app/components/common/actions/buttons/GxHorizontalLine";
import { RemarksModal } from "@/app/models/dialogs/RemarksModal";
import DialogXl from "@/app/models/dialogs/DialogXl";
import BulkUploadApproveTable from "./BulkUploadApproveTable";
import SuccessModalBulkUpload from "@/app/models/dialogs/SuccessModalBulkUpload";
import { useNavigate } from "react-router-dom";
import {
  gxPaymentsBulkApprovalViewSummaryApi,
  gxBulkPaymentsListFileApprovalApi,
  GxNonceApi,
  gxPaymentsBulkReviewTransactionViewSummaryApi,
} from "@/app/apiConfig/services/bulkPaymentsPayrollApiLayer";
import type {
  PaymentsBulkApprovalViewSummaryResponse,
  PaymentsBulkReviewTransViewSummaryResponse,
  PayrollReviewTransactionViewSummaryResponse,
} from "@/app/types/bulkPaymentsPayroll";
import DialogInviteUsers from "@/app/models/dialogs/DialogInviteUsers";
import BulkUploadIndividualDetails from "./BulkUploadIndividualDetails";
import type { BulkUploadApproveProp } from "../BulkUpload";
import SearchInput from "@/app/components/common/actions/inputs-b/SearchInput";
import { generatePkcePair } from "@/app/services/Login/pkceService";
import type { VerifierAndChallenge } from "@/app/types/payments";
import { useAppRoutingType } from "@/app/hooks/useAppRoutingType";
import { t } from "i18next";
import NoDataFound from "@/app/components/common/warning/NoDataFound";
import { Loader } from "@progress/kendo-react-indicators";
import { NONCE_TYPE } from "../nonceTypes";
import { useSelector } from "react-redux";
import type { RootState } from "@/app/redux/store";
import GXEmailOtpVerificationModal from "@/app/models/dialogs/GxEmailOtpVerificationModal";
import GxConfirmPasswordModal from "@/app/models/dialogs/GxConfimPasswordModal";
import {
  gxGenerateOtpApi,
  gxVerifyGOTPApi,
} from "@/app/apiConfig/services/paymentsAbstractApiLayer";
import { Constants } from "@/app/utils/AppConstants";
import type { ConfirmPasswordModal } from "@/app/types/payments";

const BulkUploadApprove = ({
  isOpenBulkList,
  setIsOpenBulkList,
  access,
  setCallCount,
  initialSelectedRecord,
  initialSelectedRecordKey,
}: BulkUploadApproveProp & {
  initialSelectedRecord?: PaymentsBulkApprovalViewSummaryResponse | null;
  initialSelectedRecordKey?: number;
}) => {
  const navigate = useNavigate();
  const { isCB } = useAppRoutingType();

  // ── Redux user data (flat — LandingPage dispatches { ...appSetting, ...userSetting }) ──
  const userData = useSelector((state: RootState) => state.userData.data);
  const lfiHolder: string = userData?.lfiHolder ?? "";
  const isSelfAuth: string = userData?.isSelfAuth ?? "";
  const twoFactorAuth: string | null | undefined = userData?.twoFactorAuth;
  // lastApprove: "Y" = verify only at final approval level, "N" / null = always verify
  const lastApprove: string | null | undefined = userData?.lastApprove;

  // ── 2FA state ──────────────────────────────────────────────────────────────
  const [openEmailOtp, setOpenEmailOtp] = useState<boolean>(false);
  const [manageConfirm, setManageConfirm] = useState<ConfirmPasswordModal>({
    openPasswordModal: false,
  });
  // Stores the approval action that is waiting for 2FA to complete
  const [pendingApproval, setPendingApproval] = useState<{
    processIds: number[];
    approvalCounts: { completed: number; required: number };
  } | null>(null);

  // ── General state ──────────────────────────────────────────────────────────
  const [isLoading, setIsLoading] = useState(false);
  const [bulkUploads, setBulkUploads] = useState<
    PaymentsBulkApprovalViewSummaryResponse[]
  >([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedBulkUploads, setSelectedBulkUploads] = useState<number[]>([]);
  const [checkAll, setCheckAll] = useState(false);
  const [openRemarksModal, setOpenRemarksModal] = useState(false);
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [remarks, setRemarks] = useState("");
  const [successData, setSuccessData] = useState<any>();
  const [bulkApproveCount, setBulkApproveCount] = useState<string>();
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [selectedRecord, setSelectedRecord] =
    useState<PaymentsBulkApprovalViewSummaryResponse | null>(null);
  const [selectedAction, setSelectedAction] = useState<"A" | "R" | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState("10");
  const [verifierAndChallenge, setVerifierAndChallenge] =
    useState<VerifierAndChallenge>({ verifier: "", challenge: "" });

  // ADD after existing useState declarations:
  const [approvalSummary, setApprovalSummary] = useState<
    | PaymentsBulkReviewTransViewSummaryResponse
    | PayrollReviewTransactionViewSummaryResponse
    | null
  >(null);

  // ── Search / filter ────────────────────────────────────────────────────────
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
    fetchBulkUploads(true, value);
  };

  const filteredBulkUploads = useMemo(() => {
    if (!searchQuery.trim()) return bulkUploads;
    const lowerQuery = searchQuery.toLowerCase();
    return bulkUploads.filter(
      (upload) =>
        upload.gxJobChnNo?.toLowerCase().includes(lowerQuery) ||
        upload.fileName?.toLowerCase().includes(lowerQuery) ||
        upload.jobStatusDesc?.toLowerCase().includes(lowerQuery),
    );
  }, [bulkUploads, searchQuery]);

  // ── Sync checkAll ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (filteredBulkUploads.length === 0) {
      setCheckAll(false);
      return;
    }
    const selectableCount = Math.min(filteredBulkUploads.length, 5);
    const allSelectableChosen =
      selectedBulkUploads.length >= selectableCount &&
      filteredBulkUploads
        .slice(0, selectableCount)
        .every((upload) => selectedBulkUploads.includes(upload.gxJobProcessid));
    setCheckAll(allSelectableChosen);
  }, [selectedBulkUploads, filteredBulkUploads]);

  // ── Nonce ──────────────────────────────────────────────────────────────────
  const fetchNonce = async (): Promise<string> => {
    try {
      const result = await GxNonceApi({ type: NONCE_TYPE.APPROVE });
      if (result?.status === 1 && result?.data?.nonceId) {
        return result.data.nonceId;
      }
    } catch {}
    return "";
  };

  // ── 2FA: determine whether verification is required ────────────────────────
  //
  // Mirrors the getVerifyType logic from the example:
  //   twoFactorAuth = null/undefined  → no 2FA at all  (existing flow unchanged)
  //   lastApprove   = "Y" + isCB     → skip 2FA (CB/LFI handles auth via Altareq)
  //   lastApprove   = "Y" + !isCB    → verify only at final approval ← we ARE at final
  //   lastApprove   = "N" / null     → always verify when twoFactorAuth is set
  const shouldRun2FA = (isFinalApprovalLevel: boolean): boolean => {
    if (!twoFactorAuth) return false; // null/undefined → no 2FA
    if (lastApprove === "Y" && isCB) return false; // CB + onlyFinalApprove → skip
    if (lastApprove === "Y" && !isFinalApprovalLevel) return false; // onlyFinalApprove + not final → skip
    return true;
  };

  // ── 2FA: OTP ───────────────────────────────────────────────────────────────
  const generateOtpApi = async (): Promise<void> => {
    try {
      const result = await gxGenerateOtpApi();
      if (!openEmailOtp && result?.status === 1) {
        setOpenEmailOtp(true);
      } else {
        setPendingApproval(null);
      }
    } catch {
      setPendingApproval(null);
    }
  };

  const handleSubmitOtp = async (code: string): Promise<boolean> => {
    try {
      const result = await gxVerifyGOTPApi({ flowType: "userOtp", code });
      return result?.status === 1 && result?.data?.success === true;
    } catch {
      return false;
    }
  };

  const resumePendingApproval = () => {
    if (!pendingApproval) return;
    approveOrRejectRecords(
      pendingApproval.processIds,
      "A",
      undefined,
      pendingApproval.approvalCounts,
    );
    setPendingApproval(null);
  };

  const handleOtpVerified = () => {
    setOpenEmailOtp(false);
    resumePendingApproval();
  };

  const handleCancelOtpVerification = () => {
    setOpenEmailOtp(false);
    setPendingApproval(null);
  };

  // ── 2FA: confirm password ──────────────────────────────────────────────────
  const handleConfirmPasswordModal = () => {
    setManageConfirm((prev) => ({ ...prev, openPasswordModal: false }));
    resumePendingApproval();
  };

  const handleClosePasswordModal = () => {
    setManageConfirm((prev) => ({ ...prev, openPasswordModal: false }));
    setPendingApproval(null);
  };

  // ── Fetch pending bulk uploads ─────────────────────────────────────────────
  const fetchBulkUploads = async (resetData = false, query = "") => {
    setIsLoading(true);
    try {
      if (resetData) setBulkUploads([]);
      const payload = { pageNo: currentPage, pageSize, searchText: query };
      const response = await gxPaymentsBulkApprovalViewSummaryApi(payload);
      if (response?.status === 1 && Array.isArray(response.data)) {
        const pendingRecords = response.data.filter(
          (record: PaymentsBulkApprovalViewSummaryResponse) =>
            record.jobStatusCode === 4515,
        );
        setBulkUploads(pendingRecords);
      } else {
        setBulkUploads([]);
      }
    } catch {
      setBulkUploads([]);
    } finally {
      setIsLoading(false);
    }
  };

  // ── Dialog handlers ────────────────────────────────────────────────────────
  const handleCloseDialog = () => {
    setIsOpenBulkList(false);
    setSelectedBulkUploads([]);
    setCheckAll(false);
    setSearchQuery("");
  };

  const handleRowClick = (event: any) => {
    const upload = event?.dataItem;
    if (upload) {
      setSelectedRecord(upload);
      setShowEditUserModal(true);
    }
  };

  const handleCloseEditModal = () => {
    setShowEditUserModal(false);
    setSelectedRecord(null);
    setApprovalSummary(null); // ← reset on close
  };

  // ── Approve / Reject ───────────────────────────────────────────────────────
  const approveOrRejectRecords = async (
    processIds: number[],
    aprdStatus: "A" | "R",
    remarksText?: string,
    approvalCounts?: { completed: number; required: number },
  ) => {
    if (processIds.length === 0) return;

    const currentNonceId = await fetchNonce();
    setIsLoading(true);

    try {
      const payload = {
        gxJobProcessid: processIds,
        authzType: "BULK",
        aprdStatus,
        remark: remarksText ?? "",
        nonceId: currentNonceId,
      };

      const result = await gxBulkPaymentsListFileApprovalApi(payload);

      if (result?.status === 1) {
        const totalCount = processIds.length;
        // Single-record: show approval level progress (e.g. "1/2")
        // Bulk / reject: show process-ID count (e.g. "3/3")
        const displayCount =
          approvalCounts && processIds.length === 1
            ? `${approvalCounts.completed}/${approvalCounts.required}`
            : `${totalCount}/${totalCount}`;

        setBulkApproveCount(displayCount);
        setSuccessData({ aprdStatus });
        setOpenSuccessModal(true);
        setSelectedBulkUploads([]);
        setCheckAll(false);
        setCallCount(true);
        await fetchBulkUploads();
      }
    } catch {
    } finally {
      setIsLoading(false);
      setShowEditUserModal(false);
      setOpenRemarksModal(false);
    }
  };

  // ── Single-record approve ──────────────────────────────────────────────────
  const handleApproveSingleRecord = async () => {
    if (!selectedRecord) return;
    setSelectedAction("A");
    setIsLoading(true);

    // Fetch fresh review summary — list API returns stale numberOfApproved=0
    let summary: any = null;
    try {
      const detailResponse =
        await gxPaymentsBulkReviewTransactionViewSummaryApi({
          gxJobProcessId: selectedRecord.gxJobProcessid,
        });
      if (detailResponse?.status === 1 && detailResponse.data) {
        summary = detailResponse.data;
      }
    } catch {}

    setIsLoading(false);

    // Resolve approval counts — priority: detail API → metromap → list record → default
    const numberOfApproved: number =
      summary?.numberOfApproved ??
      summary?.paymentMetromap?.approvalDetails?.approvalsCompleted ??
      (selectedRecord as any).numberOfApproved ??
      0;

    const numberOfLevels: number =
      summary?.numberOfLevels ??
      summary?.paymentMetromap?.approvalDetails?.approvalsRequired ??
      (selectedRecord as any).numberOfLevels ??
      1;

    const completedAfterThis = numberOfApproved + 1;
    const isFinalApproval = completedAfterThis >= numberOfLevels;

    // ── INTERMEDIATE: more approvers still needed ────────────────────────────
    // completedAfterThis < numberOfLevels → call API directly → success modal
    if (!isFinalApproval) {
      const counts = {
        completed: completedAfterThis,
        required: numberOfLevels,
      };

      if (shouldRun2FA(false)) {
        setPendingApproval({
          processIds: [selectedRecord.gxJobProcessid],
          approvalCounts: counts,
        });
        switch (twoFactorAuth) {
          case Constants.otp:
            await generateOtpApi();
            return;
          case Constants.gOtp:
            setOpenEmailOtp(true);
            return;
          default:
            // Unknown 2FA type — fall through to direct call
            setPendingApproval(null);
            break;
        }
      }

      approveOrRejectRecords(
        [selectedRecord.gxJobProcessid],
        "A",
        undefined,
        counts,
      );
      return;
    }

    // ── FINAL: lfiHolder=Y & isSelfAuth=Y → Tppaltareqflow ──────────────────
    // PermissionScreen owns the nonce fetch + final approval API call
    if (lfiHolder === "Y" && isSelfAuth === "Y") {
      const enrichedBulkData = {
        gxJobProcessid: selectedRecord.gxJobProcessid,
        fileName: selectedRecord.fileName,
        totalAmount: selectedRecord.totalAmount,
        baseCurrency: selectedRecord.baseCurrency,
        totalTxnCount: selectedRecord.totalTxnCount ?? selectedRecord.totalTxn,
        crtdDt: selectedRecord.crtdDt ?? selectedRecord.uploadedOn,
        fileFormat: selectedRecord.fileFormat,
        gxJobChnNo: selectedRecord.gxJobChnNo,
        gxReferenceNo: selectedRecord.gxReferenceNo,
        growxRefNo: selectedRecord.gxJobChnNo,
        uploadedOn: selectedRecord.crtdDt ?? "",
        totalTxn: selectedRecord.totalTxnCount ?? "--",
        validTxn: selectedRecord.validTxnCount ?? "--",
        invalidTxn: selectedRecord.invalidTxnCount ?? "--",
        debitAmount: selectedRecord.totalAmount ?? "--",
        status: selectedRecord.jobStatusDesc ?? "",

        // subFeatureId and countryCode come from the detail summary API
        // (same fields BulkPaymentReview passes via reviewData?.subFeatureId)
        subFeatureId: (approvalSummary as any)?.subFeatureId ?? undefined,
        countryCode: (approvalSummary as any)?.countryCode ?? undefined,
      };

      navigate("/bulk-uploads/cbuae/Tppaltareqflow", {
        state: {
          bulkData: enrichedBulkData,
          bulkType: "PAYMENT",
          flowType: "approve",
          approvePayload: {
            gxJobProcessids: [selectedRecord.gxJobProcessid],
            authzType: "BULK",
            remark: "",
            nonceId: "", // PermissionScreen fetches its own fresh nonce
          },
        },
      });
      return;
    }
    

    // ── FINAL: lfiHolder=N / isSelfAuth=N → check 2FA then call API ─────────
    //
    // shouldRun2FA logic:
    //   twoFactorAuth = null    → false → existing flow (direct API, no 2FA)
    //   lastApprove   = null    → true  → run 2FA if twoFactorAuth is set
    //   lastApprove   = "Y" + isCB  → false → CB handles auth via LFI, skip
    //   lastApprove   = "Y" + !isCB → true  → verify at final approval (DB)
    //   lastApprove   = "N"    → true  → always verify
    const finalCounts = {
      completed: numberOfLevels,
      required: numberOfLevels,
    };

    if (shouldRun2FA(true)) {
      setPendingApproval({
        processIds: [selectedRecord.gxJobProcessid],
        approvalCounts: finalCounts,
      });
      switch (twoFactorAuth) {
        case Constants.otp:
          await generateOtpApi();
          return;
        case Constants.gOtp:
          setOpenEmailOtp(true);
          return;
        default:
          // Unknown 2FA type — fall through to direct API call
          setPendingApproval(null);
          break;
      }
    }

    // twoFactorAuth=null / not applicable / unknown type → call API directly
    // This is the existing flow — unchanged when twoFactorAuth=null, lastApprove=null
    approveOrRejectRecords(
      [selectedRecord.gxJobProcessid],
      "A",
      undefined,
      finalCounts,
    );
  };

  // ── Reject handlers ────────────────────────────────────────────────────────
  const handleRejectSingleRecord = () => {
    setSelectedAction("R");
    setOpenRemarksModal(true);
  };

  const submitRejectSingle = () => {
    if (!selectedRecord) return;
    approveOrRejectRecords([selectedRecord.gxJobProcessid], "R", remarks);
    setRemarks("");
  };

  // ── Bulk handlers ──────────────────────────────────────────────────────────
  const handleBulkApprove = () => {
    if (selectedBulkUploads.length === 0 || selectedBulkUploads.length > 5)
      return;
    setSelectedAction("A");
    approveOrRejectRecords(selectedBulkUploads, "A");
  };

  const handleBulkReject = () => {
    if (selectedBulkUploads.length === 0 || selectedBulkUploads.length > 5)
      return;
    setSelectedAction("R");
    setOpenRemarksModal(true);
  };

  const confirmBulkAction = () => {
    if (selectedBulkUploads.length > 0) {
      if (selectedAction === "R") {
        approveOrRejectRecords(selectedBulkUploads, "R", remarks);
      } else if (selectedAction === "A") {
        approveOrRejectRecords(selectedBulkUploads, "A", remarks);
      }
    } else {
      submitRejectSingle();
    }
    setRemarks("");
  };

  const closeSuccessModal = () => {
    setOpenSuccessModal(false);
    setSuccessData(undefined);
    setBulkApproveCount(undefined);
  };

  // ── Effects ────────────────────────────────────────────────────────────────
  useEffect(() => {
    const initPkce = async () => {
      const { verifier, challenge } = await generatePkcePair();
      setVerifierAndChallenge({ verifier, challenge });
    };
    initPkce();
  }, []);

  useEffect(() => {
    if (isOpenBulkList) {
      fetchBulkUploads(false, searchQuery);
      setSelectedBulkUploads([]);
      setCheckAll(false);
      setSearchQuery("");
    }
  }, [isOpenBulkList, currentPage]);

  useEffect(() => {
    if (initialSelectedRecord) {
      setSelectedRecord(initialSelectedRecord);
      setShowEditUserModal(true);
    }
  }, [initialSelectedRecordKey]);

  // ── Render ─────────────────────────────────────────────────────────────────
  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="gx-converging-spinner">
          <Loader size="large" type="converging-spinner" />
        </div>
      );
    }

    if (bulkUploads.length > 0) {
      return (
        <BulkUploadApproveTable
          approveAccess={access?.approve}
          showAll={isOpenBulkList}
          onRowClick={handleRowClick}
          selectedBulkUploads={selectedBulkUploads}
          setSelectedBulkUploads={setSelectedBulkUploads}
          checkAll={checkAll}
          setCheckAll={setCheckAll}
          bulkUploads={bulkUploads}
          maxSelectionLimit={5}
          isLoading={isLoading}
          hideCbCheckbox={isCB}
        />
      );
    }

    return <NoDataFound className="gx-no-data" message={t("no_data_found")} />;
  };

  return (
    <>
      <DialogXl
        open={isOpenBulkList}
        handleClose={handleCloseDialog}
        handleFooterSubmit={handleBulkApprove}
        title={t("bulk_upload")}
        showFooter={false}
        subtitle={t("approval_list")}
        cancel={t("close")}
        submit={t("approve_selected")}
        requiredMessage={t("please_select_at_least_one_upload")}
      >
        <div className="gx-approve-required-modal relative">
          <div>
            <div className="mt-3">
              <SearchInput
                searchQuery={searchQuery}
                onSearchChange={handleSearchChange}
                placeholder={t(
                  "search_by_growx_reference_no_file_name_or_uploader",
                )}
              />
            </div>
            <div className="mt-3">{renderContent()}</div>
          </div>

          {!isCB && selectedBulkUploads.length > 0 && access?.approve && (
            <div className="gx-dialog-actions mt-4">
              <GxHorizontalLine />
              {selectedBulkUploads.length > 5 && (
                <div className="mt-3 mb-3 p-3 bg-warning bg-opacity-10 rounded">
                  <p className="gx-p12-b text-warning mb-0">
                    {t("maximum_five_records")} {selectedBulkUploads.length - 5}{" "}
                    {t("records")}
                  </p>
                </div>
              )}
              <div className="flex space-x-2 mt-4">
                <GxRedRejectButton
                  onClick={handleBulkReject}
                  disabled={isLoading || selectedBulkUploads.length > 5}
                >
                  {t("reject")}
                </GxRedRejectButton>
                <GxPrimaryButton
                  handleSubmit={handleBulkApprove}
                  label={t("approve")}
                  disabled={isLoading || selectedBulkUploads.length > 5}
                />
              </div>
            </div>
          )}
        </div>
      </DialogXl>

      {showEditUserModal && selectedRecord && (
        <DialogInviteUsers
          isOpen={showEditUserModal}
          handleClose={handleCloseEditModal}
          title={t("approval")}
          subtitle={t("required")}
          label={t("back_")}
          label1={t("reject")}
          submit={t("approve")}
          onDeleteClick={handleRejectSingleRecord}
          onResendClick={handleCloseEditModal}
          handleFooterSubmit={handleApproveSingleRecord}
          isShowBtn={false}
        >
          <div className="gx-approve-required-modal relative">
            <div className="mt-3">
              <BulkUploadIndividualDetails
                bulkData={selectedRecord}
                bulkType="PAYMENT"
                isApprovalMode={true}
                onSummaryLoaded={setApprovalSummary}
              />
              <div className="gx-dialog-actions mt-4">
                <GxHorizontalLine />
                <div className="flex space-x-2 mt-4">
                  <GxRedRejectButton
                    onClick={handleRejectSingleRecord}
                    disabled={isLoading}
                  >
                    {t("reject")}
                  </GxRedRejectButton>
                  <GxPrimaryButton
                    handleSubmit={handleApproveSingleRecord}
                    label={t("approve")}
                    disabled={isLoading}
                  />
                </div>
              </div>
            </div>
          </div>
        </DialogInviteUsers>
      )}

      {openRemarksModal && (
        <RemarksModal
          isOpen={openRemarksModal}
          onClose={() => {
            setOpenRemarksModal(false);
            setRemarks("");
          }}
          errors={{}}
          setValue={() => {}}
          isReject={selectedAction === "R"}
          value={remarks}
          onChange={setRemarks}
          submit={confirmBulkAction}
        />
      )}

      {openSuccessModal && (successData || bulkApproveCount) && (
        <SuccessModalBulkUpload
          onClose={closeSuccessModal}
          isOpen={openSuccessModal}
          successData={{ aprdStatus: selectedAction || "A" }}
          successMessage={selectedAction === "A" ? "Approved" : "Rejected"}
          bulkApproveCount={bulkApproveCount}
          onNavigate={() => navigate("/dashboard")}
        />
      )}

      {openEmailOtp && (
        <GXEmailOtpVerificationModal
          onCancel={handleCancelOtpVerification}
          onVerified={handleOtpVerified}
          onSubmit={handleSubmitOtp}
        />
      )}

      <GxConfirmPasswordModal
        open={manageConfirm.openPasswordModal}
        onClose={handleClosePasswordModal}
        onConfirm={handleConfirmPasswordModal}
      />
    </>
  );
};

export default BulkUploadApprove;

// import { useEffect, useMemo, useState } from "react";
// import GxRedRejectButton from "@/app/components/common/actions/buttons/GxRedRejectButton";
// import GxPrimaryButton from "@/app/components/common/actions/buttons/GxPrimaryButton";
// import GxHorizontalLine from "@/app/components/common/actions/buttons/GxHorizontalLine";
// import { RemarksModal } from "@/app/models/dialogs/RemarksModal";
// import DialogXl from "@/app/models/dialogs/DialogXl";
// import BulkUploadApproveTable from "./BulkUploadApproveTable";
// import SuccessModalBulkUpload from "@/app/models/dialogs/SuccessModalBulkUpload";
// import { useNavigate } from "react-router-dom";
// import {
//   gxPaymentsBulkApprovalViewSummaryApi,
//   gxBulkPaymentsListFileApprovalApi,
//   GxNonceApi,
//   gxPaymentsBulkReviewTransactionViewSummaryApi,
// } from "@/app/apiConfig/services/bulkPaymentsPayrollApiLayer";
// import type { PaymentsBulkApprovalViewSummaryResponse } from "@/app/types/bulkPaymentsPayroll";
// import DialogInviteUsers from "@/app/models/dialogs/DialogInviteUsers";
// import BulkUploadIndividualDetails from "./BulkUploadIndividualDetails";
// import type { BulkUploadApproveProp } from "../BulkUpload";
// import SearchInput from "@/app/components/common/actions/inputs-b/SearchInput";
// import { generatePkcePair } from "@/app/services/Login/pkceService";
// import type {
//   VerifierAndChallenge,
//   ConfirmPasswordModal,
// } from "@/app/types/payments";
// import { useAppRoutingType } from "@/app/hooks/useAppRoutingType";
// import { t } from "i18next";
// import NoDataFound from "@/app/components/common/warning/NoDataFound";
// import { Loader } from "@progress/kendo-react-indicators";
// import { NONCE_TYPE } from "../nonceTypes";
// import { useSelector } from "react-redux";
// import type { RootState } from "@/app/redux/store";
// import GXEmailOtpVerificationModal from "@/app/models/dialogs/GxEmailOtpVerificationModal";
// import GxConfirmPasswordModal from "@/app/models/dialogs/GxConfimPasswordModal";
// import {
//   gxGenerateOtpApi,
//   gxVerifyGOTPApi,
// } from "@/app/apiConfig/services/paymentsAbstractApiLayer";
// import { Constants } from "@/app/utils/AppConstants";

// const BulkUploadApprove = ({
//   isOpenBulkList,
//   setIsOpenBulkList,
//   access,
//   setCallCount,
//   initialSelectedRecord,
//   initialSelectedRecordKey,
// }: BulkUploadApproveProp & {
//   initialSelectedRecord?: PaymentsBulkApprovalViewSummaryResponse | null;
//   initialSelectedRecordKey?: number;
// }) => {
//   const navigate = useNavigate();
//   const { isCB } = useAppRoutingType();

//   const userData = useSelector((state: RootState) => state.userData.data);
//   const lfiHolder: string = userData?.lfiHolder ?? "";
//   const isSelfAuth: string = userData?.isSelfAuth ?? "";
//   const twoFactorAuth = userData?.twoFactorAuth;
//   const lastApprove = userData?.lastApprove;

//   const [openEmailOtp, setOpenEmailOtp] = useState<boolean>(false);
//   const [manageConfirm, setManageConfirm] = useState<ConfirmPasswordModal>({
//     openPasswordModal: false,
//   });

//   const [pendingApproval, setPendingApproval] = useState<{
//     processIds: number[];
//     approvalCounts: { completed: number; required: number };
//   } | null>(null);

//   const [isLoading, setIsLoading] = useState(false);
//   const [bulkUploads, setBulkUploads] = useState<
//     PaymentsBulkApprovalViewSummaryResponse[]
//   >([]);
//   const [searchQuery, setSearchQuery] = useState("");
//   const [selectedBulkUploads, setSelectedBulkUploads] = useState<number[]>([]);
//   const [checkAll, setCheckAll] = useState(false);
//   const [openRemarksModal, setOpenRemarksModal] = useState(false);
//   const [openSuccessModal, setOpenSuccessModal] = useState(false);
//   const [remarks, setRemarks] = useState("");
//   const [successData, setSuccessData] = useState<any>();
//   const [bulkApproveCount, setBulkApproveCount] = useState<string>();
//   const [showEditUserModal, setShowEditUserModal] = useState(false);
//   const [selectedRecord, setSelectedRecord] =
//     useState<PaymentsBulkApprovalViewSummaryResponse | null>(null);
//   const [selectedAction, setSelectedAction] = useState<"A" | "R" | null>(null);
//   const [currentPage, setCurrentPage] = useState(1);
//   const [pageSize] = useState("10");
//   const [nonceId, setNonceId] = useState<string>("");

//   const [verifierAndChallenge, setVerifierAndChallenge] =
//     useState<VerifierAndChallenge>({ verifier: "", challenge: "" });

//   const handleSearchChange = (value: string) => {
//     setSearchQuery(value);
//     setCurrentPage(1);
//     fetchBulkUploads(true, value);
//   };

//   const filteredBulkUploads = useMemo(() => {
//     if (!searchQuery.trim()) return bulkUploads;
//     const lowerQuery = searchQuery.toLowerCase();
//     return bulkUploads.filter(
//       (upload) =>
//         upload.gxJobChnNo?.toLowerCase().includes(lowerQuery) ||
//         upload.fileName?.toLowerCase().includes(lowerQuery) ||
//         upload.jobStatusDesc?.toLowerCase().includes(lowerQuery),
//     );
//   }, [bulkUploads, searchQuery]);

//   // === Sync checkAll =========================================================
//   useEffect(() => {
//     if (filteredBulkUploads.length === 0) {
//       setCheckAll(false);
//       return;
//     }
//     const selectableCount = Math.min(filteredBulkUploads.length, 5);
//     const allSelectableChosen =
//       selectedBulkUploads.length >= selectableCount &&
//       filteredBulkUploads
//         .slice(0, selectableCount)
//         .every((upload) => selectedBulkUploads.includes(upload.gxJobProcessid));
//     setCheckAll(allSelectableChosen);
//   }, [selectedBulkUploads, filteredBulkUploads]);

//   // === Fetch nonce =========================================================
//   const fetchNonce = async (): Promise<string> => {
//     try {
//       const result = await GxNonceApi({ type: NONCE_TYPE.APPROVE });
//       if (result?.status === 1 && result?.data?.nonceId) {
//         setNonceId(result.data.nonceId);
//         return result.data.nonceId;
//       }
//     } catch {}
//     return "";
//   };

//   // === 2FA: OTP generate / verify ============================================
//   const generateOtpApi = async () => {
//     try {
//       const result = await gxGenerateOtpApi();
//       if (openEmailOtp === false && result?.status === 1) {
//         setOpenEmailOtp(true);
//       } else {
//         setPendingApproval(null);
//       }
//     } catch (err) {
//       setPendingApproval(null);
//       console.log("Error in generateOtpApi::", err);
//     }
//   };

//   const handleSubmitOtp = async (code: string): Promise<boolean> => {
//     try {
//       const payload = { flowType: "userOtp", code };
//       const result = await gxVerifyGOTPApi(payload);
//       return result?.status === 1 && result?.data?.success === true;
//     } catch (err) {
//       console.log("Error in verifyGOTPApi::", err);
//       return false;
//     }
//   };

//   const resumePendingApproval = () => {
//     if (!pendingApproval) return;
//     approveOrRejectRecords(
//       pendingApproval.processIds,
//       "A",
//       undefined,
//       pendingApproval.approvalCounts,
//     );
//     setPendingApproval(null);
//   };

//   const handleOtpVerified = () => {
//     setOpenEmailOtp(false);
//     resumePendingApproval();
//   };

//   const handleCancelOtpVerification = () => {
//     setOpenEmailOtp(false);
//     setPendingApproval(null);
//   };

//   // === 2FA: confirm password =================================================
//   const handleConfirmPasswordModal = () => {
//     resumePendingApproval();
//   };

//   const handleClosePasswordModal = () => {
//     setManageConfirm((prev) => ({ ...prev, openPasswordModal: false }));
//     setPendingApproval(null);
//   };

//   // === Fetch pending bulk uploads ============================================
//   const fetchBulkUploads = async (resetData = false, query = "") => {
//     setIsLoading(true);
//     try {
//       if (resetData) {
//         setBulkUploads([]);
//       }

//       const payload = {
//         pageNo: currentPage,
//         pageSize: pageSize,
//         searchText: query,
//       };
//       const response = await gxPaymentsBulkApprovalViewSummaryApi(payload);
//       if (response?.status === 1 && Array.isArray(response.data)) {
//         const pendingRecords = response.data.filter(
//           (record: PaymentsBulkApprovalViewSummaryResponse) =>
//             record.jobStatusCode === 4515,
//         );
//         setBulkUploads(pendingRecords);
//       } else {
//         setBulkUploads([]);
//       }
//     } catch {
//       setBulkUploads([]);
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   const handleCloseDialog = () => {
//     setIsOpenBulkList(false);
//     setSelectedBulkUploads([]);
//     setCheckAll(false);
//     setSearchQuery("");
//   };

//   const handleRowClick = (event: any) => {
//     const upload = event?.dataItem;
//     if (upload) {
//       setSelectedRecord(upload);
//       setShowEditUserModal(true);
//     }
//   };

//   // === Approve / Reject handler =============================================
//   const approveOrRejectRecords = async (
//     processIds: number[],
//     aprdStatus: "A" | "R",
//     remarksText?: string,
//     approvalCounts?: { completed: number; required: number },
//   ) => {
//     if (processIds.length === 0) return;

//     const currentNonceId = await fetchNonce();

//     setIsLoading(true);
//     try {
//       const payload = {
//         gxJobProcessid: processIds,
//         authzType: "BULK",
//         aprdStatus,
//         remark: remarksText ?? "",
//         nonceId: currentNonceId,
//       };

//       const result = await gxBulkPaymentsListFileApprovalApi(payload);

//       if (result?.status === 1) {
//         const totalCount = processIds.length;

//         const displayCount =
//           approvalCounts && processIds.length === 1
//             ? `${approvalCounts.completed}/${approvalCounts.required}`
//             : `${totalCount}/${totalCount}`;

//         setBulkApproveCount(displayCount);
//         setSuccessData({ aprdStatus });

//         setOpenSuccessModal(true);
//         setSelectedBulkUploads([]);
//         setCheckAll(false);
//         setCallCount(true);
//         await fetchBulkUploads();
//       }
//     } catch {
//     } finally {
//       setIsLoading(false);
//       setShowEditUserModal(false);
//       setOpenRemarksModal(false);
//     }
//   };

//   const handleApproveSingleRecord = async () => {
//     if (!selectedRecord) return;
//     setSelectedAction("A");
//     setIsLoading(true);

//     let summary: any = null;
//     try {
//       const detailResponse =
//         await gxPaymentsBulkReviewTransactionViewSummaryApi({
//           gxJobProcessId: selectedRecord.gxJobProcessid,
//         });
//       if (detailResponse?.status === 1 && detailResponse.data) {
//         summary = detailResponse.data;
//       }
//     } catch {}

//     setIsLoading(false);

//     const numberOfApproved: number =
//       summary?.numberOfApproved ??
//       summary?.paymentMetromap?.approvalDetails?.approvalsCompleted ??
//       (selectedRecord as any).numberOfApproved ??
//       0;

//     const numberOfLevels: number =
//       summary?.numberOfLevels ??
//       summary?.paymentMetromap?.approvalDetails?.approvalsRequired ??
//       (selectedRecord as any).numberOfLevels ??
//       1;

//     const completedAfterThis = numberOfApproved + 1;
//     const isFinalApproval = completedAfterThis >= numberOfLevels;

//     if (!isFinalApproval) {
//       approveOrRejectRecords([selectedRecord.gxJobProcessid], "A", undefined, {
//         completed: numberOfApproved + 1,
//         required: numberOfLevels,
//       });
//       return;
//     }

//     if (lfiHolder === "Y" && isSelfAuth === "Y") {
//       const enrichedBulkData = {
//         gxJobProcessid: selectedRecord.gxJobProcessid,
//         fileName: selectedRecord.fileName,
//         totalAmount: selectedRecord.totalAmount,
//         baseCurrency: selectedRecord.baseCurrency,
//         totalTxnCount: selectedRecord.totalTxnCount ?? selectedRecord.totalTxn,
//         crtdDt: selectedRecord.crtdDt ?? selectedRecord.uploadedOn,
//         fileFormat: selectedRecord.fileFormat,
//         gxJobChnNo: selectedRecord.gxJobChnNo,
//         gxReferenceNo: selectedRecord.gxReferenceNo,
//         growxRefNo: selectedRecord.gxJobChnNo,
//         uploadedOn: selectedRecord.crtdDt ?? "",
//         totalTxn: selectedRecord.totalTxnCount ?? "--",
//         validTxn: selectedRecord.validTxnCount ?? "--",
//         invalidTxn: selectedRecord.invalidTxnCount ?? "--",
//         debitAmount: selectedRecord.totalAmount ?? "--",
//         status: selectedRecord.jobStatusDesc ?? "",
//       };

//       navigate("/bulk-uploads/cbuae/Tppaltareqflow", {
//         state: {
//           bulkData: enrichedBulkData,
//           bulkType: "PAYMENT",
//           flowType: "approve",
//           approvePayload: {
//             gxJobProcessids: [selectedRecord.gxJobProcessid],
//             authzType: "BULK",
//             remark: "",
//             nonceId: "",
//           },
//         },
//       });
//       return;
//     }

//     const finalApprovalCounts = {
//       completed: numberOfLevels,
//       required: numberOfLevels,
//     };

//     const shouldRun2FA = (() => {
//       if (!twoFactorAuth) return false;
//       if (lastApprove === "Y" && isCB) return false;
//       return true;
//     })();

//     if (shouldRun2FA) {
//       switch (twoFactorAuth) {
//         case Constants.otp:
//           setPendingApproval({
//             processIds: [selectedRecord.gxJobProcessid],
//             approvalCounts: finalApprovalCounts,
//           });
//           await generateOtpApi();
//           return;

//         case Constants.gOtp:
//           setPendingApproval({
//             processIds: [selectedRecord.gxJobProcessid],
//             approvalCounts: finalApprovalCounts,
//           });
//           setOpenEmailOtp(true);
//           return;

//         default:
//           break;
//       }
//     }

//     approveOrRejectRecords(
//       [selectedRecord.gxJobProcessid],
//       "A",
//       undefined,
//       finalApprovalCounts,
//     );
//   };

//   const handleRejectSingleRecord = () => {
//     setSelectedAction("R");
//     setOpenRemarksModal(true);
//   };

//   const submitRejectSingle = () => {
//     if (!selectedRecord) return;
//     approveOrRejectRecords([selectedRecord.gxJobProcessid], "R", remarks);
//     setRemarks("");
//   };

//   const handleBulkApprove = () => {
//     if (selectedBulkUploads.length === 0 || selectedBulkUploads.length > 5)
//       return;
//     setSelectedAction("A");
//     approveOrRejectRecords(selectedBulkUploads, "A");
//   };

//   const handleBulkReject = () => {
//     if (selectedBulkUploads.length === 0 || selectedBulkUploads.length > 5)
//       return;
//     setSelectedAction("R");
//     setOpenRemarksModal(true);
//   };

//   const confirmBulkAction = () => {
//     if (selectedBulkUploads.length > 0) {
//       if (selectedAction === "R") {
//         approveOrRejectRecords(selectedBulkUploads, "R", remarks);
//       } else if (selectedAction === "A") {
//         approveOrRejectRecords(selectedBulkUploads, "A", remarks);
//       }
//     } else {
//       submitRejectSingle();
//     }
//     setRemarks("");
//   };

//   const closeSuccessModal = () => {
//     setOpenSuccessModal(false);
//     setSuccessData(undefined);
//     setBulkApproveCount(undefined);
//   };

//   const handleCloseEditModal = () => {
//     setShowEditUserModal(false);
//     setSelectedRecord(null);
//   };

//   // === Mount: generate PKCE pair ============================================
//   useEffect(() => {
//     const initPkce = async () => {
//       const { verifier, challenge } = await generatePkcePair();
//       setVerifierAndChallenge({ verifier, challenge });
//     };
//     initPkce();
//   }, []);

//   useEffect(() => {
//     if (isOpenBulkList) {
//       fetchBulkUploads(false, searchQuery);
//       setSelectedBulkUploads([]);
//       setCheckAll(false);
//       setSearchQuery("");
//     }
//   }, [isOpenBulkList, currentPage]);

//   useEffect(() => {
//     if (initialSelectedRecord) {
//       setSelectedRecord(initialSelectedRecord);
//       setShowEditUserModal(true);
//     }
//   }, [initialSelectedRecordKey]);

//   const renderContent = () => {
//     if (isLoading) {
//       return (
//         <div className="gx-converging-spinner">
//           <Loader size="large" type="converging-spinner" />
//         </div>
//       );
//     }

//     if (bulkUploads.length > 0) {
//       return (
//         <BulkUploadApproveTable
//           approveAccess={access?.approve}
//           showAll={isOpenBulkList}
//           onRowClick={handleRowClick}
//           selectedBulkUploads={selectedBulkUploads}
//           setSelectedBulkUploads={setSelectedBulkUploads}
//           checkAll={checkAll}
//           setCheckAll={setCheckAll}
//           bulkUploads={bulkUploads}
//           maxSelectionLimit={5}
//           isLoading={isLoading}
//           hideCbCheckbox={isCB}
//         />
//       );
//     }

//     return <NoDataFound className="gx-no-data" message={t("no_data_found")} />;
//   };

//   return (
//     <>
//       <DialogXl
//         open={isOpenBulkList}
//         handleClose={handleCloseDialog}
//         handleFooterSubmit={handleBulkApprove}
//         title={t("bulk_upload")}
//         showFooter={false}
//         subtitle={t("approval_list")}
//         cancel={t("close")}
//         submit={t("approve_selected")}
//         requiredMessage={t("please_select_at_least_one_upload")}
//       >
//         <div className="gx-approve-required-modal relative">
//           <div>
//             <div className="mt-3">
//               <SearchInput
//                 searchQuery={searchQuery}
//                 onSearchChange={handleSearchChange}
//                 placeholder={t(
//                   "search_by_growx_reference_no_file_name_or_uploader",
//                 )}
//               />
//             </div>
//             <div className="mt-3">{renderContent()}</div>
//           </div>

//           {!isCB && selectedBulkUploads.length > 0 && access?.approve && (
//             <div className="gx-dialog-actions mt-4">
//               <GxHorizontalLine />
//               {selectedBulkUploads.length > 5 && (
//                 <div className="mt-3 mb-3 p-3 bg-warning bg-opacity-10 rounded">
//                   <p className="gx-p12-b text-warning mb-0">
//                     {t("maximum_five_records")} {selectedBulkUploads.length - 5}{" "}
//                     {t("records")}
//                   </p>
//                 </div>
//               )}
//               <div className="flex space-x-2 mt-4">
//                 <GxRedRejectButton
//                   onClick={handleBulkReject}
//                   disabled={isLoading || selectedBulkUploads.length > 5}
//                 >
//                   {t("reject")}
//                 </GxRedRejectButton>
//                 <GxPrimaryButton
//                   handleSubmit={handleBulkApprove}
//                   label={t("approve")}
//                   disabled={isLoading || selectedBulkUploads.length > 5}
//                 />
//               </div>
//             </div>
//           )}
//         </div>
//       </DialogXl>

//       {showEditUserModal && selectedRecord && (
//         <DialogInviteUsers
//           isOpen={showEditUserModal}
//           handleClose={handleCloseEditModal}
//           title={t("approval")}
//           subtitle={t("required")}
//           label={t("back_")}
//           label1={t("reject")}
//           submit={t("approve")}
//           onDeleteClick={handleRejectSingleRecord}
//           onResendClick={handleCloseEditModal}
//           handleFooterSubmit={handleApproveSingleRecord}
//           isShowBtn={false}
//         >
//           <div className="gx-approve-required-modal relative">
//             <div className="mt-3">
//               <BulkUploadIndividualDetails
//                 bulkData={selectedRecord}
//                 bulkType="PAYMENT"
//                 isApprovalMode={true}
//               />
//               <div className="gx-dialog-actions mt-4">
//                 <GxHorizontalLine />
//                 <div className="flex space-x-2 mt-4">
//                   <GxRedRejectButton
//                     onClick={handleRejectSingleRecord}
//                     disabled={isLoading}
//                   >
//                     {t("reject")}
//                   </GxRedRejectButton>
//                   <GxPrimaryButton
//                     handleSubmit={handleApproveSingleRecord}
//                     label={t("approve")}
//                     disabled={isLoading}
//                   />
//                 </div>
//               </div>
//             </div>
//           </div>
//         </DialogInviteUsers>
//       )}

//       {openRemarksModal && (
//         <RemarksModal
//           isOpen={openRemarksModal}
//           onClose={() => {
//             setOpenRemarksModal(false);
//             setRemarks("");
//           }}
//           errors={{}}
//           setValue={() => {}}
//           isReject={selectedAction === "R"}
//           value={remarks}
//           onChange={setRemarks}
//           submit={confirmBulkAction}
//         />
//       )}

//       {openSuccessModal && (successData || bulkApproveCount) && (
//         <SuccessModalBulkUpload
//           onClose={closeSuccessModal}
//           isOpen={openSuccessModal}
//           successData={{ aprdStatus: selectedAction || "A" }}
//           successMessage={selectedAction === "A" ? "Approved" : "Rejected"}
//           bulkApproveCount={bulkApproveCount}
//           onNavigate={() => navigate("/dashboard")}
//         />
//       )}

//       {openEmailOtp && (
//         <GXEmailOtpVerificationModal
//           onCancel={handleCancelOtpVerification}
//           onVerified={handleOtpVerified}
//           onSubmit={handleSubmitOtp}
//         />
//       )}

//       <GxConfirmPasswordModal
//         open={manageConfirm.openPasswordModal}
//         onClose={handleClosePasswordModal}
//         onConfirm={handleConfirmPasswordModal}
//       />
//     </>
//   );
// };

// export default BulkUploadApprove;
